// src/components/PerteVol.jsx
// Module Pertes & Vols — événements patrimoniaux non-transactionnels
// Patch : client expert/collaborateur, prix d'acquisition, devise, PDF enrichi, œuvre retrouvée.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import { supabase } from "@/lib/supabaseClient";

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const LOSS_TYPES = [
  { value: "vol", color: "#991b1b", bg: "#fef2f2" },
  { value: "perte", color: "#92400e", bg: "#fef9e7" },
  { value: "destruction", color: "#7c3aed", bg: "#f5f3ff" },
  { value: "incendie", color: "#c2410c", bg: "#fff7ed" },
  { value: "inondation", color: "#1e40af", bg: "#eff6ff" },
  { value: "autre", color: "#374151", bg: "#f9fafb" },
];

const getLossType = (value, t) => {
  const type = LOSS_TYPES.find((item) => item.value === value) || LOSS_TYPES[5];
  return { ...type, label: t(`perteVol.lossTypes.${type.value}`) };
};

const formatAmount = (value, currency = "EUR", locale = "fr-FR") => {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const currencyCode = currency === "€" ? "EUR" : (currency || "EUR");
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    })
      .format(numeric)
      .replace(/[\u00a0\u202f]/g, " ");
  } catch {
    return `${numeric.toLocaleString(locale)} ${currency || "EUR"}`;
  }
};

const formatDate = (value, locale = "fr-FR") => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(locale);
  } catch {
    return value;
  }
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, index) => currentYear - index);

const safeMaybeSingle = async (query) => {
  try {
    const { data, error } = await query.maybeSingle();
    if (error) return null;
    return data || null;
  } catch {
    return null;
  }
};

const artworkSelectFields = [
  "id",
  "title",
  "artist",
  "inventory_number",
  "estimated_value",
  "current_estimated_value",
  "valuation_estimated_value",
  "valuation_market_value",
  "valuation_consensus",
  "acquisition_price",
  "price",
  "currency",
  "client_id",
].join(", ");

const getArtworkDeclaredValue = (artwork) => (
  artwork?.current_estimated_value ??
  artwork?.valuation_estimated_value ??
  artwork?.valuation_market_value ??
  artwork?.valuation_consensus ??
  artwork?.estimated_value ??
  artwork?.price ??
  ""
);

const getArtworkPurchasePrice = (artwork) => (
  artwork?.acquisition_price ??
  artwork?.price ??
  ""
);

const loadAccessibleArtwork = async (artworkId) => {
  if (!artworkId) return null;

  const secure = await safeMaybeSingle(
    supabase.from("artworks_secure").select(artworkSelectFields).eq("id", artworkId)
  );
  if (secure) return secure;

  const direct = await safeMaybeSingle(
    supabase.from("artworks").select(artworkSelectFields).eq("id", artworkId)
  );
  if (direct) return direct;

  const shared = await safeMaybeSingle(
    supabase.from("v_artworks_shared_with_me").select(artworkSelectFields).eq("id", artworkId)
  );
  return shared || null;
};

const loadAccessibleArtworks = async (ownerClientId, currentUserId) => {
  if (!ownerClientId && !currentUserId) return [];

  if (ownerClientId && ownerClientId === currentUserId) {
    const { data } = await supabase
      .from("artworks")
      .select(artworkSelectFields)
      .eq("client_id", ownerClientId)
      .order("inventory_number", { ascending: true, nullsFirst: false });
    return data || [];
  }

  let secureQuery = supabase
    .from("artworks_secure")
    .select(artworkSelectFields)
    .order("inventory_number", { ascending: true, nullsFirst: false });

  if (ownerClientId) secureQuery = secureQuery.eq("client_id", ownerClientId);

  const { data: secure, error: secureError } = await secureQuery;
  if (!secureError && secure?.length) return secure;

  let sharedQuery = supabase
    .from("v_artworks_shared_with_me")
    .select(artworkSelectFields)
    .order("inventory_number", { ascending: true, nullsFirst: false });

  if (ownerClientId) sharedQuery = sharedQuery.eq("client_id", ownerClientId);

  const { data: shared } = await sharedQuery;
  return shared || [];
};

const enrichLossesWithCreators = async (items = []) => {
  const ids = [...new Set(items.flatMap((loss) => [loss.created_by, loss.updated_by, loss.recovered_by, loss.client_id]).filter(Boolean))];
  if (!ids.length) return items;

  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, role, plan")
    .in("id", ids);

  const byId = new Map((profiles || []).map((profile) => [profile.id, profile]));

  return items.map((loss) => ({
    ...loss,
    created_by_profile: byId.get(loss.created_by || loss.client_id) || null,
    updated_by_profile: byId.get(loss.updated_by) || null,
    recovered_by_profile: byId.get(loss.recovered_by) || null,
  }));
};

const getActorLabel = (profile, fallbackId, t) => {
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  if (fallbackId) {
    return t("perteVol.actor.user", { id: String(fallbackId).slice(0, 8) });
  }
  return t("perteVol.actor.unknown");
};

/* ── Composant principal ─────────────────────────────────────────────────── */

export default function PerteVol() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "fr";
  const locale = language.startsWith("de")
    ? "de-DE"
    : language.startsWith("en")
      ? "en-GB"
      : "fr-FR";
  const formatMoney = useCallback(
    (value, currency = "EUR") => formatAmount(value, currency, locale),
    [locale]
  );
  const formatLocalizedDate = useCallback(
    (value) => formatDate(value, locale),
    [locale]
  );

  const [searchParams] = useSearchParams();
  const routeArtworkId = searchParams.get("artwork_id");
  const routeClientId = searchParams.get("client_id");
  const [losses, setLosses] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [ownerClientId, setOwnerClientId] = useState(null);
  const [routeArtwork, setRouteArtwork] = useState(null);

  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLoss, setSelectedLoss] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [destructionFile, setDestructionFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [recoveringId, setRecoveringId] = useState(null);

  const emptyForm = useMemo(() => ({
    artwork_id: "",
    artwork_title: "",
    artist: "",
    inventory_number: "",
    loss_type: "vol",
    loss_date: todayISO(),
    tax_year: currentYear,
    declared_value: "",
    purchase_price: "",
    currency: "EUR",
    police_report: false,
    police_ref: "",
    insurance_claim: false,
    insurance_ref: "",
    indemnity: "",
    location_at_loss: "",
    notes: "",
  }), []);

  const [form, setForm] = useState(emptyForm);

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setLoading(false);
      return;
    }

    const currentUserId = session.user.id;
    setUserId(currentUserId);

    const resolvedArtwork = await loadAccessibleArtwork(routeArtworkId);
    setRouteArtwork(resolvedArtwork);

    const resolvedClientId = routeClientId || resolvedArtwork?.client_id || currentUserId;
    setOwnerClientId(resolvedClientId);

    const accessibleArtworks = await loadAccessibleArtworks(resolvedClientId, currentUserId);
    const mergedArtworks = resolvedArtwork
      ? [resolvedArtwork, ...accessibleArtworks.filter((artwork) => artwork.id !== resolvedArtwork.id)]
      : accessibleArtworks;

    setArtworks(mergedArtworks);

    let lossQuery = supabase
      .from("artwork_losses")
      .select("*")
      .eq("client_id", resolvedClientId)
      .order("loss_date", { ascending: false });

    if (routeArtworkId && !routeClientId) {
      lossQuery = lossQuery.eq("artwork_id", routeArtworkId);
    }

    const { data: loadedLosses, error } = await lossQuery;
    if (!error) {
      const enriched = await enrichLossesWithCreators(loadedLosses || []);
      setLosses(enriched);
    } else {
      console.error("Erreur chargement pertes/vols:", error);
      setLosses([]);
    }

    setLoading(false);
  }, [routeArtworkId, routeClientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!routeArtwork || routeClientId) return;
    setForm((current) => ({
      ...current,
      artwork_id: routeArtwork.id,
      artwork_title: routeArtwork.title || "",
      artist: routeArtwork.artist || "",
      inventory_number: routeArtwork.inventory_number || "",
      declared_value: getArtworkDeclaredValue(routeArtwork),
      purchase_price: getArtworkPurchasePrice(routeArtwork),
      currency: routeArtwork.currency || "EUR",
    }));
  }, [routeArtwork, routeClientId]);

  const handleArtworkSelect = (artworkId) => {
    const artwork = artworks.find((item) => item.id === artworkId);
    if (!artwork) {
      setForm((current) => ({ ...current, artwork_id: "", artwork_title: "", artist: "", inventory_number: "" }));
      return;
    }

    setForm((current) => ({
      ...current,
      artwork_id: artwork.id,
      artwork_title: artwork.title || "",
      artist: artwork.artist || "",
      inventory_number: artwork.inventory_number || "",
      declared_value: getArtworkDeclaredValue(artwork),
      purchase_price: getArtworkPurchasePrice(artwork),
      currency: artwork.currency || "EUR",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId) return;

    let destructionProofUrl = null;
    if (form.loss_type === "destruction" && destructionFile) {
      setUploadingProof(true);
      try {
        const ext = destructionFile.name.split(".").pop();
        const fileName = `destruction_proof_${Date.now()}.${ext}`;
        const filePath = `documents/${ownerClientId || userId}/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(filePath, destructionFile, { upsert: true });

        if (!uploadError) {
          const { data: signedData } = await supabase.storage
            .from("documents")
            .createSignedUrl(filePath, 3600 * 24 * 365);
          destructionProofUrl = signedData?.signedUrl || null;
        }
      } catch (error) {
        console.error("Erreur upload preuve:", error);
      } finally {
        setUploadingProof(false);
      }
    }

    const payload = {
      client_id: ownerClientId || userId,
      created_by: userId,
      updated_by: userId,
      artwork_id: form.artwork_id || null,
      artwork_title: form.artwork_title || "",
      artist: form.artist || null,
      inventory_number: form.inventory_number || null,
      loss_type: form.loss_type,
      loss_date: form.loss_date,
      tax_year: Number(form.tax_year),
      declared_value: toNumberOrNull(form.declared_value),
      purchase_price: toNumberOrNull(form.purchase_price),
      currency: form.currency || "EUR",
      police_report: Boolean(form.police_report),
      police_ref: form.police_ref || null,
      insurance_claim: Boolean(form.insurance_claim),
      insurance_ref: form.insurance_ref || null,
      indemnity: toNumberOrNull(form.indemnity),
      location_at_loss: form.location_at_loss || null,
      notes: form.notes || null,
      documents: destructionProofUrl
        ? [{ type: "preuve_destruction", url: destructionProofUrl, name: destructionFile?.name, uploaded_at: new Date().toISOString() }]
        : [],
    };

    let { data, error } = await supabase
      .from("artwork_losses")
      .insert(payload)
      .select()
      .single();

    if (error && /created_by|updated_by/i.test(error.message || "")) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.created_by;
      delete fallbackPayload.updated_by;
      const retry = await supabase.from("artwork_losses").insert(fallbackPayload).select().single();
      data = retry.data;
      error = retry.error;
    }

    if (error) {
      console.error("Erreur insertion perte/vol:", error);
      alert(t("perteVol.messages.saveError", { message: error.message }));
      return;
    }

    const enriched = (await enrichLossesWithCreators([{ ...data, created_by: data.created_by || userId }]))[0];
    setLosses((current) => [enriched, ...current]);
    setForm(emptyForm);
    setDestructionFile(null);
    setShowAddModal(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("perteVol.messages.deleteConfirm"))) return;
    const { error } = await supabase.from("artwork_losses").delete().eq("id", id);
    if (!error) {
      setLosses((current) => current.filter((loss) => loss.id !== id));
      setShowDetailsModal(false);
    }
  };

  const markAsRecovered = async (loss) => {
    if (!loss?.id || !userId) return;
    if (!window.confirm(t("perteVol.messages.recoverConfirm"))) return;

    const recoveredNotes = window.prompt(t("perteVol.messages.recoveryNotePrompt"), loss.recovered_notes || "") || null;
    const recoveredAt = todayISO();
    setRecoveringId(loss.id);

    const payload = {
      recovered_at: recoveredAt,
      recovered_by: userId,
      recovered_notes: recoveredNotes,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    };

    let { data, error } = await supabase
      .from("artwork_losses")
      .update(payload)
      .eq("id", loss.id)
      .select()
      .single();

    if (error && /recovered_by|recovered_notes/i.test(error.message || "")) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.recovered_by;
      delete fallbackPayload.recovered_notes;
      const retry = await supabase
        .from("artwork_losses")
        .update(fallbackPayload)
        .eq("id", loss.id)
        .select()
        .single();
      data = retry.data;
      error = retry.error;
    }

    setRecoveringId(null);

    if (error) {
      console.error("Erreur récupération œuvre:", error);
      alert(t("perteVol.messages.recoverError"));
      return;
    }

    const enriched = (await enrichLossesWithCreators([data]))[0];
    setLosses((current) => current.map((item) => (item.id === loss.id ? enriched : item)));
    setSelectedLoss((current) => (current?.id === loss.id ? enriched : current));
  };

  const filtered = losses.filter((loss) => {
    if (typeFilter !== "all" && loss.loss_type !== typeFilter) return false;
    if (yearFilter !== "all" && String(loss.tax_year) !== yearFilter) return false;
    return true;
  });

  const years = [...new Set(losses.map((loss) => loss.tax_year).filter(Boolean))].sort((a, b) => b - a);

  const existingArtworkLosses = useMemo(() => {
    if (!form.artwork_id) return [];
    return losses
      .filter((loss) => loss.artwork_id === form.artwork_id && !loss.recovered_at)
      .sort((a, b) => new Date(b.created_at || b.loss_date) - new Date(a.created_at || a.loss_date));
  }, [form.artwork_id, losses]);

  const statsLosses = yearFilter === "all" ? losses : losses.filter((loss) => String(loss.tax_year) === yearFilter);
  const totalDeclared = statsLosses.reduce((sum, loss) => sum + (Number(loss.declared_value) || 0), 0);
  const totalPurchase = statsLosses.reduce((sum, loss) => sum + (Number(loss.purchase_price) || 0), 0);
  const totalIndemnity = statsLosses.reduce((sum, loss) => sum + (Number(loss.indemnity) || 0), 0);
  const netLoss = totalDeclared - totalIndemnity;
  const countVol = statsLosses.filter((loss) => loss.loss_type === "vol").length;
  const activeLossesCount = statsLosses.filter((loss) => !loss.recovered_at).length;
  const recoveredCount = statsLosses.filter((loss) => Boolean(loss.recovered_at)).length;

  const exportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const tableWidth = pageWidth - margin * 2;
    let y = 18;

    const gold = [180, 150, 80];
    const red = [153, 27, 27];
    const black = [26, 26, 26];
    const gray = [120, 120, 120];

    const checkPage = (needed = 16) => {
      if (y + needed > pageHeight - 20) {
        doc.addPage();
        y = 18;
      }
    };

    doc.setFillColor(...gold);
    doc.rect(0, 0, pageWidth, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...black);
    doc.text(t("perteVol.pdf.title"), margin, y + 8);
    y += 13;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    const yearLabel = yearFilter === "all"
      ? t("perteVol.pdf.allYears")
      : t("perteVol.pdf.taxYear", { year: yearFilter });
    doc.text(`${yearLabel} — ${formatLocalizedDate(new Date())}`, margin, y);
    y += 10;

    doc.setFillColor(26, 26, 26);
    doc.rect(margin, y, tableWidth, 28, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    doc.text(t("perteVol.stats.events"), margin + 4, y + 6);
    doc.text(t("perteVol.stats.declaredValue"), margin + 36, y + 6);
    doc.text(t("perteVol.pdf.purchasePriceShort"), margin + 75, y + 6);
    doc.text(t("perteVol.stats.indemnities"), margin + 116, y + 6);
    doc.text(t("perteVol.stats.netLoss"), margin + 154, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text(String(statsLosses.length), margin + 4, y + 18);
    doc.setTextColor(...gold);
    doc.text(formatMoney(totalDeclared), margin + 36, y + 18);
    doc.setTextColor(255, 255, 255);
    doc.text(formatMoney(totalPurchase), margin + 75, y + 18);
    doc.setTextColor(100, 200, 130);
    doc.text(formatMoney(totalIndemnity), margin + 116, y + 18);
    doc.setTextColor(...red);
    doc.text(formatMoney(netLoss), margin + 154, y + 18);
    y += 34;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text(t("perteVol.pdf.activeRecovered", { active: activeLossesCount, recovered: recoveredCount }), margin, y);
    y += 8;

    if (countVol > 0) {
      checkPage(14);
      doc.setFillColor(254, 242, 242);
      doc.rect(margin, y, tableWidth, 10, "F");
      doc.setFillColor(153, 27, 27);
      doc.rect(margin, y, 2, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(153, 27, 27);
      doc.text(t("perteVol.pdf.theftAlert", { count: countVol }), margin + 5, y + 6.5);
      y += 14;
    }

    doc.setFillColor(240, 237, 232);
    doc.rect(margin, y, tableWidth, 9, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...black);
    doc.text(t("perteVol.table.type"), margin + 2, y + 6);
    doc.text(t("perteVol.table.date"), margin + 18, y + 6);
    doc.text(t("perteVol.pdf.artworkArtist"), margin + 35, y + 6);
    doc.text(t("perteVol.pdf.declaredShort"), margin + 86, y + 6);
    doc.text(t("perteVol.pdf.purchaseShort"), margin + 112, y + 6);
    doc.text(t("perteVol.pdf.indemnityShort"), margin + 137, y + 6);
    doc.text(t("perteVol.table.status"), margin + 160, y + 6);
    y += 11;

    filtered.forEach((loss, index) => {
      checkPage(15);
      const lossType = getLossType(loss.loss_type, t);
      if (index % 2 === 1) {
        doc.setFillColor(252, 251, 249);
        doc.rect(margin, y, tableWidth, 14, "F");
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(loss.loss_type === "vol" ? 153 : 100, loss.loss_type === "vol" ? 27 : 80, 27);
      doc.text(lossType.label.toUpperCase(), margin + 2, y + 5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...black);
      doc.text(formatLocalizedDate(loss.loss_date), margin + 18, y + 5);

      const title = doc.splitTextToSize(loss.artwork_title || t("perteVol.common.untitled"), 48)[0];
      doc.text(title, margin + 35, y + 5);
      doc.setTextColor(...gray);
      doc.text(loss.artist || "—", margin + 35, y + 9.5);

      doc.setTextColor(...black);
      doc.text(formatMoney(loss.declared_value, loss.currency), margin + 86, y + 5);
      doc.text(formatMoney(loss.purchase_price, loss.currency), margin + 112, y + 5);
      doc.setTextColor(40, 140, 80);
      doc.text(formatMoney(loss.indemnity, loss.currency), margin + 137, y + 5);

      doc.setTextColor(loss.recovered_at ? 40 : 153, loss.recovered_at ? 120 : 27, loss.recovered_at ? 80 : 27);
      doc.text(
        loss.recovered_at
          ? t("perteVol.pdf.recoveredDate", { date: formatLocalizedDate(loss.recovered_at) })
          : t("perteVol.status.active"),
        margin + 160,
        y + 5
      );

      doc.setDrawColor(230, 227, 222);
      doc.line(margin, y + 14, margin + tableWidth, y + 14);
      y += 15;
    });

    const withRecoveryNotes = filtered.filter((loss) => loss.recovered_notes);
    if (withRecoveryNotes.length > 0) {
      checkPage(20);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...black);
      doc.text(t("perteVol.pdf.recoveryNotesTitle"), margin, y);
      y += 7;
      withRecoveryNotes.forEach((loss) => {
        checkPage(14);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text(
          t("perteVol.pdf.recoveryNoteLine", {
            artwork: loss.artwork_title || t("perteVol.common.untitled"),
            date: formatLocalizedDate(loss.recovered_at),
          }),
          margin,
          y
        );
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...gray);
        doc.splitTextToSize(loss.recovered_notes, tableWidth).forEach((line) => {
          checkPage(5);
          doc.text(line, margin, y);
          y += 4;
        });
        y += 2;
      });
    }

    const withNotes = filtered.filter((loss) => loss.notes);
    if (withNotes.length > 0) {
      checkPage(20);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...black);
      doc.text(t("perteVol.pdf.eventNotesTitle"), margin, y);
      y += 7;
      withNotes.forEach((loss) => {
        checkPage(14);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text(
          t("perteVol.pdf.eventNoteLine", {
            artwork: loss.artwork_title || t("perteVol.common.untitled"),
            artist: loss.artist || "",
            date: formatLocalizedDate(loss.loss_date),
          }),
          margin,
          y
        );
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...gray);
        doc.splitTextToSize(loss.notes, tableWidth).forEach((line) => {
          checkPage(5);
          doc.text(line, margin, y);
          y += 4;
        });
        y += 2;
      });
    }

    checkPage(20);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(...gray);
    doc.splitTextToSize(
      t("perteVol.pdf.disclaimer"),
      tableWidth
    ).forEach((line) => {
      checkPage(5);
      doc.text(line, margin, y);
      y += 4;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFillColor(...gold);
      doc.rect(0, pageHeight - 3, pageWidth, 3, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...gray);
      doc.text(t("perteVol.pdf.footer"), pageWidth / 2, pageHeight - 6, { align: "center" });
      doc.text(t("perteVol.pdf.page", { page, total: pageCount }), pageWidth - margin, pageHeight - 6, { align: "right" });
    }

    doc.save(t("perteVol.pdf.filename", { year: yearFilter === "all" ? t("perteVol.pdf.filenameAll") : yearFilter }));
  };

  const renderRecoveryBadge = (loss) => {
    if (!loss.recovered_at) return null;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        {t("perteVol.recoveredOn", { date: formatLocalizedDate(loss.recovered_at) })}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
      <div className="mb-8">
        <p className="text-xs tracking-widest text-gray-400 uppercase mb-1">{t("perteVol.eyebrow")}</p>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-3xl font-serif text-gray-900 mb-2">{t("perteVol.title")}</h1>
            <p className="text-sm text-gray-500 max-w-xl">
              {t("perteVol.subtitle")}
            </p>
            {routeArtworkId && ownerClientId && (
              <Link
                to={`/pertes-vols?client_id=${ownerClientId}`}
                className="inline-flex mt-3 text-sm font-medium text-gray-700 underline hover:text-black"
              >
                {t("perteVol.viewAllClientLosses")}
              </Link>
            )}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-artsy btn-primary-artsy self-start sm:self-auto whitespace-nowrap"
          >
            {t("perteVol.declare")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {[
          { label: t("perteVol.stats.events"), value: statsLosses.length || "—", color: "text-gray-900" },
          { label: t("perteVol.stats.active"), value: activeLossesCount || "—", color: "text-red-700" },
          { label: t("perteVol.stats.recovered"), value: recoveredCount || "—", color: "text-green-700" },
          { label: t("perteVol.stats.declaredValue"), value: totalDeclared > 0 ? formatMoney(totalDeclared) : "—", color: "text-amber-700" },
          { label: t("perteVol.stats.purchasePrice"), value: totalPurchase > 0 ? formatMoney(totalPurchase) : "—", color: "text-gray-900" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 leading-tight">{label}</p>
            <p className={`text-xl font-light ${color} break-words`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {[
          { label: t("perteVol.stats.indemnities"), value: totalIndemnity > 0 ? formatMoney(totalIndemnity) : "—", color: "text-green-700" },
          { label: t("perteVol.stats.netLoss"), value: netLoss > 0 ? formatMoney(netLoss) : "—", color: "text-red-700" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 leading-tight">{label}</p>
            <p className={`text-xl font-light ${color} break-words`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          <select
            className="border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-black flex-1 sm:flex-none"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
          >
            <option value="all">{t("perteVol.filters.allTypes")}</option>
            {LOSS_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {t(`perteVol.lossTypes.${type.value}`)}
              </option>
            ))}
          </select>
          <select
            className="border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-black flex-1 sm:flex-none"
            value={yearFilter}
            onChange={(event) => setYearFilter(event.target.value)}
          >
            <option value="all">{t("perteVol.filters.allYears")}</option>
            {years.map((year) => <option key={year} value={String(year)}>{year}</option>)}
          </select>
        </div>
        <button onClick={exportPDF} className="btn-artsy btn-secondary-artsy text-sm self-start sm:self-auto">
          {t("perteVol.exportPdf")}
        </button>
      </div>

      <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              {[
                t("perteVol.table.type"),
                t("perteVol.table.date"),
                t("perteVol.table.artwork"),
                t("perteVol.table.artist"),
                t("perteVol.table.purchasePrice"),
                t("perteVol.table.declaredValue"),
                t("perteVol.table.indemnity"),
                t("perteVol.table.status"),
                t("perteVol.table.taxYear"),
                "",
              ].map((header) => (
                <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400 text-sm">{t("perteVol.common.loading")}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400 text-sm">{t(typeFilter !== "all" || yearFilter !== "all" ? "perteVol.empty.filtered" : "perteVol.empty.all")}</td></tr>
            ) : filtered.map((loss) => {
              const lossType = getLossType(loss.loss_type, t);
              return (
                <tr key={loss.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-full uppercase" style={{ background: lossType.bg, color: lossType.color }}>
                      {lossType.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatLocalizedDate(loss.loss_date)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {loss.artwork_title || "—"}
                    {loss.inventory_number && <div className="text-xs text-gray-400">{loss.inventory_number}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{loss.artist || "—"}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700">{formatMoney(loss.purchase_price, loss.currency)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-amber-700">{formatMoney(loss.declared_value, loss.currency)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-green-700">{formatMoney(loss.indemnity, loss.currency)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {loss.recovered_at ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        {t("perteVol.status.recovered")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                        {t("perteVol.status.active")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{loss.tax_year}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => { setSelectedLoss(loss); setShowDetailsModal(true); }}
                      className="text-sm text-gray-500 hover:text-black transition-colors"
                    >
                      {t("perteVol.common.detailsArrow")}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3 mb-8">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-400 text-sm">{t("perteVol.common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-400 text-sm">{t(typeFilter !== "all" || yearFilter !== "all" ? "perteVol.empty.filtered" : "perteVol.empty.all")}</div>
        ) : filtered.map((loss) => {
          const lossType = getLossType(loss.loss_type, t);
          return (
            <div key={loss.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <span className="text-xs font-semibold px-2 py-1 rounded-full uppercase" style={{ background: lossType.bg, color: lossType.color }}>{lossType.label}</span>
                <span className="text-xs text-gray-400">{formatLocalizedDate(loss.loss_date)}</span>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-0.5">{loss.artwork_title || "—"}</p>
              {loss.inventory_number && <p className="text-xs text-gray-400 mb-1">{loss.inventory_number}</p>}
              {loss.artist && <p className="text-sm text-gray-500 mb-3">{loss.artist}</p>}
              <div className="grid grid-cols-2 gap-2 text-xs border-t border-gray-100 pt-3">
                <span>{t("perteVol.mobile.purchasePrice")}: <strong>{formatMoney(loss.purchase_price, loss.currency)}</strong></span>
                <span>{t("perteVol.mobile.declared")}: <strong className="text-amber-700">{formatMoney(loss.declared_value, loss.currency)}</strong></span>
                <span>{t("perteVol.mobile.indemnity")}: <strong className="text-green-700">{formatMoney(loss.indemnity, loss.currency)}</strong></span>
                <span>{loss.tax_year}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                {loss.recovered_at ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    {t("perteVol.status.recovered")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                    {t("perteVol.status.active")}
                  </span>
                )}
                <button onClick={() => { setSelectedLoss(loss); setShowDetailsModal(true); }} className="text-sm text-gray-500 font-medium">{t("perteVol.common.detailsArrow")}</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-sm text-amber-900">
        <p className="font-semibold mb-1">{t("perteVol.fiscalNote.title")}</p>
        <p className="leading-relaxed">
          {t("perteVol.fiscalNote.body")}
        </p>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-xl font-serif text-gray-900">{t("perteVol.addModal.title")}</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-black text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{t("perteVol.addModal.artwork")}</label>
                <select
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded"
                  value={form.artwork_id}
                  onChange={(event) => handleArtworkSelect(event.target.value)}
                >
                  <option value="">{t("perteVol.addModal.selectArtwork")}</option>
                  {artworks.map((artwork) => (
                    <option key={artwork.id} value={artwork.id}>
                      {[artwork.inventory_number, artwork.title, artwork.artist].filter(Boolean).join(" — ")}
                    </option>
                  ))}
                </select>
              </div>

              {existingArtworkLosses.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold mb-2">{t("perteVol.duplicate.title")}</p>
                  <div className="space-y-2">
                    {existingArtworkLosses.map((loss) => {
                      const lossType = getLossType(loss.loss_type, t);
                      return (
                        <div key={loss.id} className="rounded-md bg-white/60 border border-amber-200 px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full uppercase" style={{ background: lossType.bg, color: lossType.color }}>{lossType.label}</span>
                            <span className="font-medium">{formatLocalizedDate(loss.loss_date)}</span>
                          </div>
                          <div className="text-xs text-amber-800 mt-1">
                            {t("perteVol.duplicate.createdBy", { date: formatLocalizedDate(loss.created_at), actor: getActorLabel(loss.created_by_profile, loss.created_by || loss.client_id, t) })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.artworkTitle")} *</label>
                  <input required className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.artwork_title} onChange={(event) => setForm((current) => ({ ...current, artwork_title: event.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.table.artist")}</label>
                  <input className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.artist} onChange={(event) => setForm((current) => ({ ...current, artist: event.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.inventoryNumber")}</label>
                  <input className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.inventory_number} onChange={(event) => setForm((current) => ({ ...current, inventory_number: event.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.table.type")} *</label>
                  <select required className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.loss_type} onChange={(event) => setForm((current) => ({ ...current, loss_type: event.target.value }))}>
                    {LOSS_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {t(`perteVol.lossTypes.${type.value}`)}
              </option>
            ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.eventDate")} *</label>
                  <input required type="date" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.loss_date} onChange={(event) => setForm((current) => ({ ...current, loss_date: event.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.table.taxYear")} *</label>
                  <select required className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.tax_year} onChange={(event) => setForm((current) => ({ ...current, tax_year: event.target.value }))}>
                    {YEARS.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.table.declaredValue")}</label>
                  <input type="number" min="0" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.declared_value} onChange={(event) => setForm((current) => ({ ...current, declared_value: event.target.value }))} placeholder={t("perteVol.addModal.declaredValuePlaceholder")} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.initialPurchasePrice")}</label>
                  <input type="number" min="0" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.purchase_price} onChange={(event) => setForm((current) => ({ ...current, purchase_price: event.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.currency")}</label>
                  <input maxLength={3} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded uppercase" value={form.currency} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value.toUpperCase() || "EUR" }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.indemnityReceived")}</label>
                  <input type="number" min="0" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.indemnity} onChange={(event) => setForm((current) => ({ ...current, indemnity: event.target.value }))} placeholder={t("perteVol.addModal.indemnityPlaceholder")} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.locationAtLoss")}</label>
                <input className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded" value={form.location_at_loss} onChange={(event) => setForm((current) => ({ ...current, location_at_loss: event.target.value }))} placeholder={t("perteVol.addModal.locationPlaceholder")} />
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t("perteVol.procedures.title")}</p>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.police_report} onChange={(event) => setForm((current) => ({ ...current, police_report: event.target.checked }))} />
                  {t("perteVol.procedures.policeReport")}
                </label>
                {form.police_report && <input className="w-full border border-gray-200 px-3 py-2 text-sm rounded" value={form.police_ref} onChange={(event) => setForm((current) => ({ ...current, police_ref: event.target.value }))} placeholder={t("perteVol.procedures.policeReference")} />}
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.insurance_claim} onChange={(event) => setForm((current) => ({ ...current, insurance_claim: event.target.checked }))} />
                  {t("perteVol.procedures.insuranceClaim")}
                </label>
                {form.insurance_claim && <input className="w-full border border-gray-200 px-3 py-2 text-sm rounded" value={form.insurance_ref} onChange={(event) => setForm((current) => ({ ...current, insurance_ref: event.target.value }))} placeholder={t("perteVol.procedures.insuranceReference")} />}
              </div>

              {form.loss_type === "destruction" && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-2">{t("perteVol.destructionProof.title")}</p>
                  <label className="flex flex-col items-center gap-2 border-2 border-dashed border-purple-300 rounded-lg p-4 cursor-pointer hover:border-purple-500 hover:bg-purple-100 transition">
                    <span className="text-sm text-purple-700 font-medium">{destructionFile ? destructionFile.name : t("perteVol.destructionProof.selectFile")}</span>
                    <span className="text-xs text-purple-500">{t("perteVol.destructionProof.helper")}</span>
                    <input type="file" accept="application/pdf,image/*,.doc,.docx" className="hidden" onChange={(event) => setDestructionFile(event.target.files[0] || null)} />
                  </label>
                  {destructionFile && <button type="button" onClick={() => setDestructionFile(null)} className="mt-2 text-xs text-purple-500 hover:text-purple-800">
                    {t("perteVol.destructionProof.remove")}
                  </button>}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("perteVol.addModal.notes")}</label>
                <textarea rows={3} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded resize-none" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder={t("perteVol.addModal.notesPlaceholder")} />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-artsy btn-secondary-artsy">{t("common.cancel")}</button>
                <button type="submit" disabled={uploadingProof} className="btn-artsy btn-primary-artsy disabled:opacity-50">{uploadingProof ? t("perteVol.common.uploading") : t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && selectedLoss && (() => {
        const lossType = getLossType(selectedLoss.loss_type, t);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: lossType.bg, color: lossType.color }}>{lossType.label}</span>
                  <h2 className="text-lg font-serif text-gray-900">{selectedLoss.artwork_title || t("perteVol.common.untitled")}</h2>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-black text-2xl leading-none">&times;</button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    [t("perteVol.table.artist"), selectedLoss.artist],
                    [t("perteVol.addModal.inventoryNumber"), selectedLoss.inventory_number],
                    [t("perteVol.table.date"), formatLocalizedDate(selectedLoss.loss_date)],
                    [t("perteVol.table.taxYear"), selectedLoss.tax_year],
                    [t("perteVol.table.purchasePrice"), formatMoney(selectedLoss.purchase_price, selectedLoss.currency)],
                    [t("perteVol.table.declaredValue"), formatMoney(selectedLoss.declared_value, selectedLoss.currency)],
                    [t("perteVol.table.indemnity"), formatMoney(selectedLoss.indemnity, selectedLoss.currency)],
                    [t("perteVol.details.location"), selectedLoss.location_at_loss || "—"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
                      <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t("perteVol.table.status")}</p>
                  {selectedLoss.recovered_at ? (
                    <div className="space-y-2">
                      {renderRecoveryBadge(selectedLoss)}
                      <p className="text-sm text-gray-700">{t("perteVol.details.recoveredBy", { actor: getActorLabel(selectedLoss.recovered_by_profile, selectedLoss.recovered_by, t) })}</p>
                      {selectedLoss.recovered_notes && <p className="text-sm text-gray-600">{selectedLoss.recovered_notes}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" />
                        <span className="text-sm font-medium text-red-700">{t("perteVol.details.activeLoss")}</span>
                      </div>
                      <button
                        onClick={() => markAsRecovered(selectedLoss)}
                        disabled={recoveringId === selectedLoss.id}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-700 text-white text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        {recoveringId === selectedLoss.id ? t("perteVol.common.updating") : t("perteVol.markRecovered")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t("perteVol.details.traceability")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("perteVol.details.createdOn")}</p>
                      <p className="font-medium text-gray-900">{formatLocalizedDate(selectedLoss.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("perteVol.details.createdBy")}</p>
                      <p className="font-medium text-gray-900">{getActorLabel(selectedLoss.created_by_profile, selectedLoss.created_by || selectedLoss.client_id, t)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t("perteVol.details.procedures")}</p>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${selectedLoss.police_report ? "bg-green-500" : "bg-gray-300"}`} /><span className="text-sm text-gray-700">{t("perteVol.procedures.policeReport")}</span>{selectedLoss.police_ref && <span className="text-xs text-gray-400">({selectedLoss.police_ref})</span>}</div>
                    <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${selectedLoss.insurance_claim ? "bg-green-500" : "bg-gray-300"}`} /><span className="text-sm text-gray-700">{t("perteVol.procedures.insuranceClaim")}</span>{selectedLoss.insurance_ref && <span className="text-xs text-gray-400">({selectedLoss.insurance_ref})</span>}</div>
                  </div>
                </div>

                {selectedLoss.loss_type === "destruction" && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("perteVol.destructionProof.title")}</p>
                    {Array.isArray(selectedLoss.documents) && selectedLoss.documents.length > 0 ? (
                      <a href={selectedLoss.documents[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-purple-700 underline hover:text-purple-900">{selectedLoss.documents[0].name || t("perteVol.destructionProof.viewDocument")}</a>
                    ) : <p className="text-sm text-gray-400 italic">{t("perteVol.destructionProof.none")}</p>}
                  </div>
                )}

                {selectedLoss.notes && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("perteVol.details.circumstances")}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedLoss.notes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2">
                  <button onClick={() => handleDelete(selectedLoss.id)} className="text-sm text-red-600 hover:text-red-800 transition-colors">{t("perteVol.common.delete")}</button>
                  <button onClick={() => setShowDetailsModal(false)} className="btn-artsy btn-secondary-artsy">{t("common.close")}</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}